import { Button, Input, SelectSearch, Form } from "@telluscompany/design-system";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";
import { cleanEmptyStrings } from "@shared/hooks";

import { createInspectionFilterSchema } from "../../schemas/inspectionFilter.schema";
import {
  EMPTY_INSPECTION_FILTERS,
  type InspectionFilterValues,
} from "../../utils/inspectionFilters";
import {
  INSPECTION_ORIGIN_VALUES,
  INSPECTION_STATUS_VALUES,
  inspectionOriginLabelKey,
  inspectionStatusLabelKey,
} from "../../utils/inspectionLabels";

/** Mesma pilha do `form-item` do DS: label + gap-1 + input + faixa de 20px. */
const FIELD_CELL = "min-w-0";

const DATE_FIELD_CELL = "grid min-w-0 gap-1";

const DATE_INPUT_CLASS =
  "box-border h-10 w-full min-w-0 rounded-xl border border-border-1 bg-background px-4 text-sm shadow-sm outline-none focus-visible:border-border-2";

const DATE_MESSAGE_CLASS = "min-h-[20px] text-xs";

interface InspectionsFilterPanelProps {
  initialFilters: InspectionFilterValues;
  onApply: (filters: InspectionFilterValues) => void;
  onClear: () => void;
}

/**
 * Painel `TableFilter` no padrão Admin (`AdminUsersFilter` / `AdminStructuresFilter`):
 * RHF + primitives DS, footer Limpar + Filtrar.
 */
const InspectionsFilterPanel = ({
  initialFilters,
  onApply,
  onClear,
}: InspectionsFilterPanelProps) => {
  const { translate } = useLocalization();

  const schema = useMemo(
    () => createInspectionFilterSchema(translate),
    [translate],
  );

  const form = useForm<InspectionFilterValues>({
    resolver: zodResolver(schema),
    defaultValues: { ...EMPTY_INSPECTION_FILTERS, ...initialFilters },
    shouldFocusError: true,
  });

  const dateRangeError =
    form.formState.errors.dateTo?.message ??
    form.formState.errors.dateFrom?.message;

  const statusOptions = useMemo(
    () =>
      INSPECTION_STATUS_VALUES.map((status) => ({
        value: status,
        label: translate(inspectionStatusLabelKey(status)),
      })),
    [translate],
  );

  const originOptions = useMemo(
    () =>
      INSPECTION_ORIGIN_VALUES.map((origin) => ({
        value: origin,
        label: translate(inspectionOriginLabelKey(origin)),
      })),
    [translate],
  );

  const anomaliesOptions = useMemo(
    () => [
      { value: "true", label: translate("inspection.filters.yes") },
      { value: "false", label: translate("inspection.filters.no") },
    ],
    [translate],
  );

  const handleFilter = (data: InspectionFilterValues) => {
    if (data.dateFrom && data.dateTo && data.dateFrom > data.dateTo) {
      form.setError("dateTo", {
        type: "validate",
        message: translate("inspection.filters.dateRangeInvalid"),
      });
      return;
    }
    const cleaned = cleanEmptyStrings(data) as Partial<InspectionFilterValues>;
    onApply({ ...EMPTY_INSPECTION_FILTERS, ...cleaned });
  };

  const handleClear = () => {
    form.reset(EMPTY_INSPECTION_FILTERS);
    onClear();
  };

  return (
    <Form {...form}>
      <form
        data-testid="inspection-filters"
        noValidate
        onSubmit={form.handleSubmit(handleFilter)}
      >
        <div className="grid w-full grid-cols-7 items-end gap-3">
          <div className={FIELD_CELL}>
            <Input
              control={form.control}
              label={`${translate("inspection.filters.code")}:`}
              name="inspectionCode"
              type="text"
              required={false}
              placeholder={translate("inspection.filters.codePlaceholder")}
            />
          </div>
          <div className={FIELD_CELL}>
            <Input
              control={form.control}
              label={`${translate("inspection.filters.responsible")}:`}
              name="responsibleName"
              type="text"
              required={false}
              placeholder={translate("inspection.filters.responsiblePlaceholder")}
            />
          </div>
          <div className={DATE_FIELD_CELL}>
            <label
              className="text-label"
              htmlFor="inspection-filter-date-from"
            >
              {translate("inspection.filters.dateFrom")}:
            </label>
            <input
              id="inspection-filter-date-from"
              type="date"
              aria-label={`${translate("inspection.filters.dateFrom")}:`}
              className={DATE_INPUT_CLASS}
              {...form.register("dateFrom")}
            />
            <div className={DATE_MESSAGE_CLASS} aria-hidden />
          </div>
          <div className={DATE_FIELD_CELL}>
            <label
              className={`text-label${dateRangeError ? " text-error" : ""}`}
              htmlFor="inspection-filter-date-to"
            >
              {translate("inspection.filters.dateTo")}:
            </label>
            <input
              id="inspection-filter-date-to"
              type="date"
              aria-label={`${translate("inspection.filters.dateTo")}:`}
              aria-invalid={Boolean(dateRangeError) || undefined}
              className={`${DATE_INPUT_CLASS} aria-invalid:border-error`}
              {...form.register("dateTo")}
            />
            <div
              role={dateRangeError ? "alert" : undefined}
              className={`${DATE_MESSAGE_CLASS}${dateRangeError ? " text-error" : ""}`}
            >
              {dateRangeError ?? null}
            </div>
          </div>
          <div className={FIELD_CELL}>
            <SelectSearch
              control={form.control}
              label={`${translate("inspection.filters.hasAnomalies")}:`}
              name="hasAnomalies"
              placeholder={translate("inspection.filters.allFeminine")}
              options={anomaliesOptions}
              clearable
              required={false}
            />
          </div>
          <div className={FIELD_CELL}>
            <SelectSearch
              control={form.control}
              label={`${translate("inspection.filters.status")}:`}
              name="inspectionStatus"
              placeholder={translate("inspection.filters.all")}
              options={statusOptions}
              clearable
              required={false}
            />
          </div>
          <div className={FIELD_CELL}>
            <SelectSearch
              control={form.control}
              label={`${translate("inspection.filters.origin")}:`}
              name="creationOrigin"
              placeholder={translate("inspection.filters.allFeminine")}
              options={originOptions}
              clearable
              required={false}
            />
          </div>
        </div>
        <div className="mt-5 mb-4 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate("inspection.actions.clearFilters")}
          </Button>
          <Button type="submit" iconLeft={<Filter fill="currentColor" />}>
            {translate("inspection.actions.filter")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default InspectionsFilterPanel;
